import { EntityBase } from '@provision/entity-types';
import { CustomAttributeColumnRenderer } from 'src/graphql/entities/custom-view/custom-view-config/custom-attributes-bridge';
import { CustomViewAttribute } from 'src/graphql/entities/custom-view/custom-view.types';
import { AttributeValue } from 'src/pages/CustomViews/custom-views-entities-table/attribute-value';
import {
  CustomComponentsByAttribute,
  getAttributeRawValue,
} from 'src/pages/CustomViews/custom-views-entities-table/render-attribute-value';
import { CardChipLine } from './card-chip-line';
import { CardColumns, CardLayoutRoles, isEmptyAttributeValue } from './mobile-card-layout';
import {
  CardBody,
  CardName,
  CardSubtitle,
  CardTextColumn,
  CardTitleRow,
  CardTrailingBottom,
  CardTrailingColumn,
  CardTrailingTop,
} from './mobile-card-list.elements';
import { useCardChipValueItems } from './use-card-chip-value-items';

// A custom component builds its output from the whole row, so an empty raw value can still
// render: a site without a plan shows "Non-Member", exactly as the table does.
const hasRoleValue = <T extends EntityBase>(
  record: T,
  column: CustomViewAttribute,
  customComponentsByAttribute?: CustomComponentsByAttribute,
) =>
  !!customComponentsByAttribute?.[column.attribute] ||
  !isEmptyAttributeValue(getAttributeRawValue(record, column), column);

export const TwoColumnCardBody = <T extends EntityBase>({
  record,
  layout,
  cardColumns,
  customAttributeRenderers,
  customComponentsByAttribute,
}: {
  record: T;
  layout: CardLayoutRoles;
  cardColumns: CardColumns;
  customAttributeRenderers?: Map<string, CustomAttributeColumnRenderer>;
  customComponentsByAttribute?: CustomComponentsByAttribute;
}) => {
  const chipItems = useCardChipValueItems({
    record,
    chips: cardColumns.chips,
    layout,
    customAttributeRenderers,
    customComponentsByAttribute,
  });

  const renderRole = (column?: CustomViewAttribute) =>
    column && hasRoleValue(record, column, customComponentsByAttribute) ? (
      <AttributeValue
        record={record}
        column={column}
        customComponentsByAttribute={customComponentsByAttribute}
      />
    ) : null;

  const title = renderRole(cardColumns.title);
  const subtitle = renderRole(cardColumns.subtitle);
  const trailingTop = renderRole(cardColumns.trailingTop);
  const trailingBottom = renderRole(cardColumns.trailingBottom);
  const hasHeader = !!(title || subtitle || trailingTop || trailingBottom);

  return (
    <CardBody>
      {hasHeader ? (
        <CardTitleRow>
          {title || subtitle ? (
            <CardTextColumn>
              {title ? <CardName>{title}</CardName> : null}
              {subtitle ? <CardSubtitle>{subtitle}</CardSubtitle> : null}
            </CardTextColumn>
          ) : null}
          {trailingTop || trailingBottom ? (
            <CardTrailingColumn>
              {trailingTop ? <CardTrailingTop>{trailingTop}</CardTrailingTop> : null}
              {trailingBottom ? (
                <CardTrailingBottom>{trailingBottom}</CardTrailingBottom>
              ) : null}
            </CardTrailingColumn>
          ) : null}
        </CardTitleRow>
      ) : null}
      {chipItems.length ? <CardChipLine items={chipItems} isFirstLine={!hasHeader} /> : null}
    </CardBody>
  );
};
